import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {fetchUploadedInvoiceDetail} from '../../actions/invoiceUpload.action';
import {
  INVOICE_UPLOAD_MATCHING_STATUSES,
  INVOICE_UPLOAD_COMPLETE_STATUSES,
  INVOICE_UPLOAD_TERMINAL_STATUSES,
  INVOICE_UPLOAD_POLL_INTERVAL_MS,
  INVOICE_UPLOAD_INITIAL_POLL_DELAY_MS,
  INVOICE_UPLOAD_MATCH_FALLBACK_MS,
  INVOICE_UPLOAD_PROGRESS_STEP_LABELS,
  INVOICE_UPLOAD_READING_MESSAGES,
  INVOICE_UPLOAD_READING_MESSAGE_INTERVAL_MS,
  INVOICE_UPLOAD_MATCHING_CAPTION,
  INVOICE_UPLOAD_TIMED_OUT_CAPTION,
  INVOICE_UPLOAD_STATUS_LABELS,
  INVOICE_UPLOAD_STATUS_MATCHED,
  INVOICE_UPLOAD_DEFAULT_COMPARISON_WINDOW_DAYS,
} from '../../constants/constants';
import {formatDate, formatCurrency} from '../../helpers/invoiceUpload.helper';
import {
  ComparisonHeader,
  ComparisonSubtitle,
  OverviewCard,
  OverviewItem,
  ProgressSteps,
  ProgressStep,
  ProgressDot,
  ProgressConnector,
  ProgressCaption,
  ProgressBar,
  ProgressBarFill,
  SavingsHero,
  SavingsHeroLabel,
  SavingsHeroValue,
  SavingsHeroCaption,
  SectionTitle,
  PageIntro,
  LineItemCard,
  LineItemHeader,
  LineItemDescription,
  LineItemDetails,
  LineItemMatch,
  MatchInfo,
  ProductLink,
  SavingBadge,
  NoMatchLabel,
  InferredNote,
  ProductSwapNote,
  ProductSwapLabel,
  MatchingPlaceholder,
  ProcessingMessage,
  StatusBadge,
  ErrorMessage,
} from '../../client/InvoiceUploadPage/InvoiceUploadPage.style';

const StepProgress = ({current}) => (
  <ProgressSteps>
    {INVOICE_UPLOAD_PROGRESS_STEP_LABELS.map((label, i) => {
      const state = i < current ? 'done' : i === current ? 'active' : 'pending';
      return (
        <React.Fragment key={label}>
          {i > 0 && <ProgressConnector $filled={i <= current} />}
          <ProgressStep $state={state}>
            <ProgressDot $state={state}>
              {state === 'done' ? '✓' : i + 1}
            </ProgressDot>
            {label}
          </ProgressStep>
        </React.Fragment>
      );
    })}
  </ProgressSteps>
);

StepProgress.propTypes = {
  current: PropTypes.number.isRequired,
};

// Mirrors the Price Comparison results page (InvoiceUploadDetail) for
// embedding elsewhere: the progress tracker, overview, matching placeholders,
// savings hero, details grid and item-by-item comparison. Deliberately
// leaves out the page chrome (back link, add-to-basket) that doesn't apply
// to this embedding.
const PriceComparisonPanel = ({id, history, isReferral = false}) => {
  const dispatch = useDispatch();
  const invoiceUpload = useSelector(state => state.invoiceUpload || {});

  const [pollingReady, setPollingReady] = useState(false);
  const [readingMsgIndex, setReadingMsgIndex] = useState(0);
  const [matchTimedOut, setMatchTimedOut] = useState(false);

  const detail =
    invoiceUpload.detail &&
    invoiceUpload.detail.overview &&
    String(invoiceUpload.detail.overview.id) === String(id)
      ? invoiceUpload.detail
      : null;
  // Only surface a fetch error while we don't already have a valid result for
  // this id — a stale error from a previous id (or a transient blip that a
  // later poll resolved) shouldn't linger once we do.
  const detailError = !detail ? invoiceUpload.detailError : null;

  const overview = detail && detail.overview;
  const status = overview && overview.status;
  const savingsData = detail && detail.savingsData;
  const isMatching = INVOICE_UPLOAD_MATCHING_STATUSES.indexOf(status) !== -1;
  const isComplete = INVOICE_UPLOAD_COMPLETE_STATUSES.indexOf(status) !== -1;
  const isFailed = status === 'FAILED';
  const isDuplicate = status === 'DUPLICATE';
  // An invoice from outside the service's comparison window is rejected after
  // extraction rather than matched: prices have moved on both sides, so any
  // saving computed from it would be fiction. Terminal, like DUPLICATE.
  const isTooOld = status === 'TOO_OLD';
  // Nothing invoice-like in the upload: the service deletes the pages and the
  // extracted data, so there is no overview to show and nothing to retry for —
  // only a message telling the uploader what went in.
  const isNotAnInvoice = status === 'NOT_AN_INVOICE';
  // Anything the comparison can end on, listed once in constants.js.
  const isTerminal = INVOICE_UPLOAD_TERMINAL_STATUSES.indexOf(status) !== -1;
  const comparisonWindowDays =
    (overview && overview.comparisonWindowDays) ||
    INVOICE_UPLOAD_DEFAULT_COMPARISON_WINDOW_DAYS;
  // A re-upload of an already-known invoice is flagged DUPLICATE, but it can
  // still carry its own fully-computed savingsData — don't rely solely on
  // the `matchedLineItems` summary count (which isn't always populated);
  // check the line items themselves for an actual match too, and show
  // whatever comparison data exists regardless of status.
  const hasResults =
    !!savingsData &&
    Array.isArray(savingsData.lineItems) &&
    savingsData.lineItems.length > 0 &&
    (savingsData.matchedLineItems > 0 ||
      savingsData.lineItems.some(item => !!item.matchedProductId));
  const resultsReady = isComplete || hasResults;
  const isDone = resultsReady || isTerminal;
  const currentStep = resultsReady ? 2 : isMatching ? 1 : 0;
  const inProgress = !resultsReady && !isTerminal;
  // Present as "Results ready" even when the backend still reports an
  // in-progress (or duplicate) status alongside a fully computed comparison,
  // so the badge doesn't contradict the results shown.
  const displayStatus =
    resultsReady && !isComplete ? INVOICE_UPLOAD_STATUS_MATCHED : status;
  const extractedLineItems =
    savingsData && Array.isArray(savingsData.lineItems)
      ? savingsData.lineItems
      : [];
  const matchedLineItemCount =
    savingsData &&
    (savingsData.matchedLineItems > 0
      ? savingsData.matchedLineItems
      : extractedLineItems.filter(item => !!item.matchedProductId).length);

  // Sum only the lines where JJ costs less (or the same price) — lines where
  // JJ costs more don't erode the figure. If nothing is cheaper there's no
  // saving to show.
  const totalPositiveSavings =
    savingsData && Array.isArray(savingsData.lineItems)
      ? savingsData.lineItems.reduce(
          (sum, item) =>
            item.saving != null && item.saving > 0 ? sum + item.saving : sum,
          0
        )
      : 0;

  // Comparison processing never finishes in under ~20s, so wait before the
  // first status check rather than polling a job that can't be done yet.
  useEffect(() => {
    if (!id) {
      setPollingReady(false);
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      setPollingReady(true);
    }, INVOICE_UPLOAD_INITIAL_POLL_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, [id]);

  // Poll non-silently — unlike the results page (which already has a value on
  // screen to hold onto), this panel has nothing to show yet, so a real fetch
  // failure (auth/permission/network) needs to surface rather than be
  // swallowed, or a persistent failure looks identical to "still processing".
  useEffect(() => {
    if (!pollingReady || !id || isDone) {
      return undefined;
    }
    dispatch(fetchUploadedInvoiceDetail(id, false, false, isReferral));
    const intervalId = setInterval(() => {
      dispatch(fetchUploadedInvoiceDetail(id, false, false, isReferral));
    }, INVOICE_UPLOAD_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [pollingReady, id, isDone, isReferral, dispatch]);

  // Fallback so a stalled pipeline doesn't look frozen forever — matches the
  // results page's own timeout messaging.
  useEffect(() => {
    if (!pollingReady || isDone) {
      setMatchTimedOut(false);
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      setMatchTimedOut(true);
    }, INVOICE_UPLOAD_MATCH_FALLBACK_MS);
    return () => clearTimeout(timeoutId);
  }, [pollingReady, isDone]);

  // Cycle the "reading" sub-message so the wait doesn't look frozen. Holds on
  // the last message rather than looping.
  useEffect(() => {
    if (currentStep !== 0) {
      setReadingMsgIndex(0);
      return undefined;
    }
    const timer = setInterval(() => {
      setReadingMsgIndex(i =>
        Math.min(i + 1, INVOICE_UPLOAD_READING_MESSAGES.length - 1)
      );
    }, INVOICE_UPLOAD_READING_MESSAGE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [currentStep]);

  const goToProduct = productId => e => {
    e.preventDefault();
    history.push(`/productDetail/${productId}`);
  };

  const progressCaption = matchTimedOut
    ? INVOICE_UPLOAD_TIMED_OUT_CAPTION
    : currentStep >= 1
    ? INVOICE_UPLOAD_MATCHING_CAPTION
    : INVOICE_UPLOAD_READING_MESSAGES[readingMsgIndex];

  return (
    <div style={{marginTop: '3.2rem'}}>
      <ComparisonHeader>
        <ComparisonSubtitle>
          Based on the invoice you supplied &mdash; like-for-like against JJ
          Foodservice.
        </ComparisonSubtitle>
      </ComparisonHeader>

      {detailError && <ErrorMessage>{detailError}</ErrorMessage>}

      {inProgress && (
        <>
          <StepProgress current={currentStep} />
          <ProgressCaption>{progressCaption}</ProgressCaption>
          {!matchTimedOut && (
            <ProgressBar>
              <ProgressBarFill />
            </ProgressBar>
          )}
        </>
      )}

      {/* One persistent card for the whole lifecycle — this used to be a separate
          OverviewCard pre-results and DetailsCard post-results, which meant the status
          row was unmounted and a differently-laid-out one mounted in its place the
          instant results arrived. Keeping a single card and just adding/updating fields
          in place is what stops that show/hide flash. */}
      {overview && (
        <OverviewCard>
          <OverviewItem>
            <label>Status</label>
            <StatusBadge $status={displayStatus}>
              {INVOICE_UPLOAD_STATUS_LABELS[displayStatus] || displayStatus}
            </StatusBadge>
          </OverviewItem>
          <OverviewItem>
            <label>Supplier</label>
            <span>{overview.supplierName || '-'}</span>
          </OverviewItem>
          <OverviewItem>
            <label>Invoice No.</label>
            <span>{overview.invoiceNumber || '-'}</span>
          </OverviewItem>
          {resultsReady && savingsData && matchedLineItemCount > 0 && (
            <OverviewItem>
              <label>Items matched</label>
              <span>
                {matchedLineItemCount} of {savingsData.lineItems.length}
              </span>
            </OverviewItem>
          )}
          <OverviewItem>
            <label>Invoice Date</label>
            <span>{formatDate(overview.invoiceDate)}</span>
          </OverviewItem>
          <OverviewItem>
            <label>Uploaded</label>
            <span>{formatDate(overview.uploadedAt)}</span>
          </OverviewItem>
        </OverviewCard>
      )}

      {isFailed && (
        <ErrorMessage>
          We couldn&apos;t process this invoice. Please try uploading it again
          or contact customer support if the problem persists.
        </ErrorMessage>
      )}

      {isDuplicate && !resultsReady && (
        <ProcessingMessage>
          <h3>This invoice has already been uploaded</h3>
          <p>
            We recognised this invoice from a previous upload, so we
            haven&apos;t created a duplicate comparison.
          </p>
        </ProcessingMessage>
      )}

      {isTooOld && (
        <ProcessingMessage>
          <h3>This invoice is too old to compare</h3>
          <p>
            {overview && overview.invoiceDate ? (
              <>This invoice is dated {formatDate(overview.invoiceDate)}. </>
            ) : null}
            Prices change constantly, so we only compare invoices from the last{' '}
            {comparisonWindowDays} days — anything older wouldn&apos;t give you
            a figure you could rely on. Upload a recent invoice to see how JJ
            compares.
          </p>
        </ProcessingMessage>
      )}

      {isNotAnInvoice && (
        <ProcessingMessage>
          <h3>We couldn&apos;t find an invoice in that upload</h3>
          <p>
            Nothing we could read as a supplier invoice came back from that
            file, so there&apos;s nothing to compare. Try again with a clear,
            full photo or PDF of the invoice itself.
          </p>
        </ProcessingMessage>
      )}

      {isComplete && !savingsData && (
        <ProcessingMessage>
          <h3>Savings data unavailable</h3>
          <p>
            This invoice has been processed but the detailed savings breakdown
            is not available. Please contact customer support if you need
            assistance.
          </p>
        </ProcessingMessage>
      )}

      {resultsReady && savingsData && matchedLineItemCount === 0 && (
        <ProcessingMessage>
          <h3>We couldn&apos;t compare any items on this invoice</h3>
          <p>
            We weren&apos;t able to confidently match your invoice lines to our
            catalogue.
            {overview && overview.invoiceTotal != null && (
              <>
                {' '}
                Your invoice total was {formatCurrency(overview.invoiceTotal)}.
              </>
            )}
          </p>
          <p>
            If you&apos;d like a member of the team to take a look, please
            contact customer support.
          </p>
        </ProcessingMessage>
      )}

      {resultsReady && savingsData && matchedLineItemCount > 0 && (
        <>
          {totalPositiveSavings > 0 ? (
            <SavingsHero>
              <SavingsHeroLabel>You save with JJ</SavingsHeroLabel>
              <SavingsHeroValue>
                {formatCurrency(totalPositiveSavings)}
              </SavingsHeroValue>
              <SavingsHeroCaption>
                Based on the items where JJ costs less
              </SavingsHeroCaption>
            </SavingsHero>
          ) : (
            <SavingsHero>
              <SavingsHeroCaption>
                We compared {matchedLineItemCount}{' '}
                {matchedLineItemCount === 1 ? 'item' : 'items'} on your invoice.
                On this order, JJ didn&apos;t cost less overall &mdash; but
                prices change, so it&apos;s worth checking again next time.
              </SavingsHeroCaption>
            </SavingsHero>
          )}
        </>
      )}

      {extractedLineItems.length > 0 && (
        <>
          <SectionTitle>
            {matchedLineItemCount === 0
              ? 'Items on your invoice'
              : 'Item-by-item comparison'}
          </SectionTitle>
          {matchedLineItemCount > 0 && (
            <PageIntro>
              <p>
                Each of your lines, matched to the equivalent JJ product on a
                like-for-like basis. Tap any JJ item for full details and
                availability.
              </p>
            </PageIntro>
          )}

          {extractedLineItems.map((item, idx) => (
            <LineItemCard
              key={idx}
              $noMatch={item.noMatchAvailable}
              $productSwap={item.isProductSwap}
            >
              <LineItemHeader>
                <div>
                  <LineItemDescription>{item.description}</LineItemDescription>
                  <LineItemDetails>
                    Qty: {item.invoiceQuantity} &times;{' '}
                    {formatCurrency(item.invoiceUnitPrice)} ={' '}
                    {formatCurrency(item.invoiceLineTotal)}
                  </LineItemDetails>
                </div>
              </LineItemHeader>

              {item.nonProductLine ? (
                <InferredNote>
                  Discount / credit line &ndash; nothing to match.
                </InferredNote>
              ) : item.insufficientDetail ? (
                <NoMatchLabel>
                  Unable to match product. Additional details (size, weight or
                  volume) required.
                </NoMatchLabel>
              ) : item.noMatchAvailable ? (
                <NoMatchLabel>
                  No JJ alternative available. Please contact us to discuss.
                </NoMatchLabel>
              ) : isMatching &&
                !item.matchedProductId &&
                !item.matchedProductName ? (
                // Matching resolves line by line — a poll can land savingsData with some
                // lines already matched and this one not yet, with none of the flags above
                // set either. Without this branch it fell through to the match display below
                // with nothing to show, then flipped to the real match a poll or two later —
                // the "rows refreshing" effect this guards against.
                <MatchingPlaceholder>
                  Finding you the best JJ price&hellip;
                </MatchingPlaceholder>
              ) : (
                <>
                  <LineItemMatch>
                    <MatchInfo>
                      {item.isProductSwap && (
                        <ProductSwapLabel>JJ swap</ProductSwapLabel>
                      )}
                      <strong>
                        {item.matchedProductId ? (
                          <ProductLink
                            href={`/productDetail/${item.matchedProductId}`}
                            onClick={goToProduct(item.matchedProductId)}
                          >
                            {item.matchedProductId} &ndash;{' '}
                            {item.matchedProductName}
                          </ProductLink>
                        ) : (
                          item.matchedProductName
                        )}
                      </strong>
                      JJ unit price {formatCurrency(item.matchedProductPrice)}
                      {item.effectiveTotal != null && (
                        <>
                          {' '}
                          | JJ Equivalent Total{' '}
                          {formatCurrency(item.effectiveTotal)}
                        </>
                      )}
                      {item.measurementInferred && (
                        <InferredNote>
                          We can&apos;t calculate the saving on this line
                          &ndash; your invoice didn&apos;t show a clear weight
                          or quantity.
                        </InferredNote>
                      )}
                    </MatchInfo>
                    {item.saving != null && (
                      <SavingBadge $positive={item.saving >= 0}>
                        {item.saving === 0
                          ? 'Same price'
                          : item.saving > 0
                          ? `Save ${formatCurrency(item.saving)}`
                          : `${formatCurrency(Math.abs(item.saving))} more`}
                      </SavingBadge>
                    )}
                  </LineItemMatch>
                  {item.isProductSwap && item.swappedFromProductId && (
                    <ProductSwapNote>
                      Matched brand product:{' '}
                      <ProductLink
                        href={`/productDetail/${item.swappedFromProductId}`}
                        onClick={goToProduct(item.swappedFromProductId)}
                      >
                        {item.swappedFromProductId} &ndash;{' '}
                        {item.swappedFromProductName}
                      </ProductLink>
                      {item.swappedFromProductPrice != null && (
                        <>
                          {' '}
                          &middot; JJ price:{' '}
                          {formatCurrency(item.swappedFromProductPrice)}
                        </>
                      )}
                    </ProductSwapNote>
                  )}
                </>
              )}
            </LineItemCard>
          ))}
        </>
      )}
    </div>
  );
};

PriceComparisonPanel.propTypes = {
  id: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
  // The id is a referral signup's scan (read via referral-comparison).
  isReferral: PropTypes.bool,
};

export default PriceComparisonPanel;
